import React, { useCallback, useEffect, useState } from 'react';
import { Template } from '@bynder-studio/render-core';
import { Button, ListItem, Modal } from '@bynder/design-system';
import { IconDelete, IconFolderOpen } from '@bynder/icons';
import Editable from 'packages/common/editable/Editable';

type Props = {
    preset: Template;
    selected: any;
    selectPreset: (presetId: number) => void;
    renamePreset: (presetId: number, value: any) => void;
    removePreset: (presetId: number) => void;
    unlockPresetItemAfterRemoval: () => void;
    requestingRename: boolean;
};

const PresetListItem = ({
    preset,
    selected,
    renamePreset,
    selectPreset,
    removePreset,
    unlockPresetItemAfterRemoval,
    requestingRename,
}: Props) => {
    const [editable, setEditable] = useState(false);
    const [removeModalVisible, setRemoveModalVisible] = useState(false);

    const confirmRemoving = useCallback(() => {
        setRemoveModalVisible(false);
        removePreset(preset.id);
    }, [removePreset, preset]);

    const cancelRemoving = useCallback(() => {
        unlockPresetItemAfterRemoval();
        setRemoveModalVisible(false);
    }, []);

    useEffect(() => {
        if (!requestingRename) {
            setEditable(false);
        }
    }, [requestingRename]);

    return (
        // We can't pass onClick to ListItem because in that case ListItem is considered as "focusable" element in the design-system
        // and Enter and Space will be considered as navigation actions and passed.
        <div
            role="button"
            tabIndex={0}
            onClick={() => {
                if (preset.id !== selected?.id) {
                    selectPreset(preset.id);
                }
            }}
            onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && preset.id !== selected?.id) {
                    selectPreset(preset.id);
                }
            }}
        >
            <ListItem
                key={preset.id}
                isSelected={selected && selected.id === preset.id}
                thumbnail={<IconFolderOpen />}
                rightElements={
                    !editable ? (
                        <Button
                            title="delete preset"
                            icon={<IconDelete />}
                            variant="clean"
                            onClick={() => setRemoveModalVisible(true)}
                        />
                    ) : null
                }
            >
                <Editable
                    value={preset.name}
                    editable={editable}
                    onToggle={(value) => setEditable(value)}
                    onConfirm={(value) => renamePreset(preset.id, value)}
                    alwaysShowButton={false}
                    hideIcon
                    withDoubleClick
                />
            </ListItem>
            <Modal
                isOpen={removeModalVisible}
                onClose={cancelRemoving}
                title="Delete preset"
                actionPrimary={
                    <Button variant="primary" onClick={confirmRemoving}>
                        Delete
                    </Button>
                }
                actionSecondary={<Button onClick={cancelRemoving}>Cancel</Button>}
            >
                Are you sure you want to delete this preset?
            </Modal>
        </div>
    );
};

export default PresetListItem;
